import { useCallback, useEffect, useRef, useState } from 'react';
import { BrandGroupUpdatePatch } from 'src/graphql/entities/brand-group';

type UpdateBrandGroup = (
  patch: BrandGroupUpdatePatch,
  options?: { signal?: AbortSignal },
) => Promise<unknown>;

interface UseBrandGroupAutosaveInput {
  brandGroupId: string | undefined;
  updateBrandGroup: UpdateBrandGroup;
  delayMs: number;
}

/**
 * Debounced autosave for one brand group that never lets an older save overwrite newer edits.
 *
 * - A new edit aborts the save in flight, so its stale response never reaches the cache.
 * - Edits stay in `unsaved` until a save carrying them settles, so the fields of an aborted
 *   save go out again with the next one.
 * - Render `{ ...serverCopy, ...unsaved }`: whatever the cache does meanwhile (a stale response,
 *   the optimistic layer Apollo rolls back on abort), what the user typed stays on screen.
 */
export function useBrandGroupAutosave({
  brandGroupId,
  updateBrandGroup,
  delayMs,
}: UseBrandGroupAutosaveInput) {
  const [unsaved, setUnsaved] = useState<BrandGroupUpdatePatch>({});
  const unsavedRef = useRef<BrandGroupUpdatePatch>({});
  const controllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setUnsavedPatch = useCallback((patch: BrandGroupUpdatePatch) => {
    unsavedRef.current = patch;
    setUnsaved(patch);
  }, []);

  // Switching brand group drops edits queued for the previous one. A save already in flight
  // is detached rather than aborted, so the next edit cannot cancel it: it finishes for the
  // brand group it was sent for, and its response only touches that group.
  useEffect(() => {
    controllerRef.current = null;
    setUnsavedPatch({});

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [brandGroupId, setUnsavedPatch]);

  const send = useCallback(async () => {
    timerRef.current = null;

    const sent = unsavedRef.current;
    if (!brandGroupId || !Object.keys(sent).length) {
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      await updateBrandGroup({ ...sent, id: brandGroupId }, { signal: controller.signal });
    } catch {
      // `useBrandGroup` reports failures; settling below is the same either way.
    }

    // A newer edit took over and its save carries these fields too, or the user switched
    // brand group and `unsaved` now belongs to another one. Either way, leave it alone.
    if (controller.signal.aborted || controllerRef.current !== controller) {
      return;
    }

    controllerRef.current = null;
    // Every edit aborts the save in flight, so nothing was edited since this one went out.
    // Saved, or failed for another reason: either way the server copy is the truth again.
    setUnsavedPatch({});
  }, [brandGroupId, updateBrandGroup, setUnsavedPatch]);

  const queueSave = useCallback(
    (patch: BrandGroupUpdatePatch) => {
      controllerRef.current?.abort();
      controllerRef.current = null;

      setUnsavedPatch({ ...unsavedRef.current, ...patch });

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => void send(), delayMs);
    },
    [delayMs, send, setUnsavedPatch],
  );

  return { unsaved, queueSave };
}
